import React, { useState, useEffect, useReducer } from 'react';

import { Box, Paper } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { closeCompetencySidePanelAction } from 'actions/competencySidePanelActions';
import InfiniteScroll from 'react-infinite-scroller';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import NoFeedbacksMessage from 'screens/performanceReview/components/NoFeedbacksMessage';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import styled, { css } from 'styled-components';
import {
  StyledTypography,
  StyledDivider,
  HideScrollbarCSS,
  StyledBox,
  StyledButton,
  StyledIconButton,
  VerticallyAlignedFlexContainer,
} from 'styles/common';

import { FEEDBACK_FORM_TYPE, SENTIMENTS_TYPES } from 'constants/ProjectConstants';
import { pushFeedbackParams } from 'constants/helper';
import { FeedbackIcon } from 'constants/icons';

import { Typography13 } from 'uiToolkit/Typography';

import Feedback from './Feedback';

const ADD_FEED = 'ADD_FEED';
const UPDATE_HAS_MORE = 'UPDATE_HAS_MORE';
const UPDATE_LOADING = 'UPDATE_LOADING';

function feedbackReducer(state, action) {
  switch (action.type) {
    case ADD_FEED: {
      const newState = { ...state };
      newState.data = [...state.data, ...action.data];
      return newState;
    }
    case UPDATE_LOADING: {
      const newState = { ...state };
      newState.loading = action.data;
      return newState;
    }
    case UPDATE_HAS_MORE: {
      const newState = { ...state };
      newState.hasMore = action.data;
      return newState;
    }
    default:
      return state;
  }
}

export default function Feedbacks({
  headerText = '',
  type,
  noHorizontalPadding = false,
  fetchDataFn = null,
  // employeeId is used in request/share feedback panel, in case no feedback is present.
  employeeId = null,
  setIsPraiseEmpty = () => {},
  setIsAdviceEmpty = () => {},
  accessible = false,
  isCompetencySidePanel = false,
  competencyId,
  hideShareFeedbackButtons = false,
}) {
  const [feedbackDataState, dispatchFeedbackDataState] = useReducer(feedbackReducer, {
    data: [],
    loading: false,
    hasMore: true,
  });
  const [loading, setLoading] = useState(true);
  const history = useHistory();
  const dispatch = useDispatch();

  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);

  const showShareFeedbackButton = loggedInEmployeeId !== employeeId;

  const loadFeedback = function (page) {
    const limit = 100;
    const offset = page * limit;
    setLoading(true);

    if (typeof fetchDataFn === 'function') {
      fetchDataFn(limit, offset)
        .then((data) => {
          if (data.length < limit) dispatchFeedbackDataState({ type: UPDATE_HAS_MORE, data: false });
          if (data.length > 0) dispatchFeedbackDataState({ type: ADD_FEED, data });
        })
        .catch((err) => {
          dispatchFeedbackDataState({ type: UPDATE_HAS_MORE, data: false });
        })
        .finally(() => setLoading(false));
    }
  };

  const handleRequestNewFeedbackClick = () => {
    pushFeedbackParams({
      history,
      formType: FEEDBACK_FORM_TYPE.REQUESTED,
      newForm: true,
      empId: employeeId ?? null,
      competencyId,
      source: 'Develop Side Panel',
    });
  };

  const handleShareNewFeedbackClick = async () => {
    pushFeedbackParams({
      history,
      formType: FEEDBACK_FORM_TYPE.PROVIDED,
      newForm: true,
      empId: employeeId ?? null,
      feedbackType: type,
      competencyId,
      source: 'Develop Side Panel',
    });

    dispatch(closeCompetencySidePanelAction());
  };

  useEffect(() => {
    if (!loading) {
      if (!feedbackDataState?.data?.length) {
        if (type === SENTIMENTS_TYPES.appreciative.value) {
          setIsPraiseEmpty(true);
        } else if (type === SENTIMENTS_TYPES.constructive.value) {
          setIsAdviceEmpty(true);
        }
      } else if (type === SENTIMENTS_TYPES.appreciative.value) {
        setIsPraiseEmpty(false);
      } else if (type === SENTIMENTS_TYPES.constructive.value) {
        setIsAdviceEmpty(false);
      }
    }
  }, [feedbackDataState, type, loading]);

  useEffect(() => {
    loadFeedback(0);
  }, []);

  return (
    <Box marginBottom="1rem">
      <StyledBox bottomGutter={2}>
        <VerticallyAlignedFlexContainer>
          <StyledTypography weight={600} variant="body1">
            {headerText}
          </StyledTypography>
          {/* request button */}
          {!hideShareFeedbackButtons &&
            Boolean(
              !loading &&
                // feedbackDataState.data.length !== 0 &&
                (isCompetencySidePanel ? accessible : true) &&
                !showShareFeedbackButton
            ) && (
              <StyledButton style={{ marginLeft: 'auto' }} onClick={handleRequestNewFeedbackClick}>
                <VerticallyAlignedFlexContainer>
                  <StyledIconButton color="primary" borderColor="primary" size="tinier" padding="xLarge">
                    <FeedbackIcon size="fixed20px" color="primary" />
                  </StyledIconButton>
                  <Typography13 leftGutter={1} color="primary">
                    Request Feedback
                  </Typography13>
                </VerticallyAlignedFlexContainer>
              </StyledButton>
            )}
          {/* share button */}
          {!hideShareFeedbackButtons && Boolean(!loading && showShareFeedbackButton) && (
            <StyledButton style={{ marginLeft: 'auto' }} onClick={handleShareNewFeedbackClick}>
              <VerticallyAlignedFlexContainer>
                <StyledIconButton color="primary" borderColor="primary" size="tinier" padding="xLarge">
                  <FeedbackIcon size="fixed20px" color="primary" />
                </StyledIconButton>
                <Typography13 leftGutter={1} color="primary">
                  Share Feedback
                </Typography13>
              </VerticallyAlignedFlexContainer>
            </StyledButton>
          )}
        </VerticallyAlignedFlexContainer>
      </StyledBox>
      <FeedbackContainer $noHorizontalPadding={noHorizontalPadding}>
        <InfiniteScroll
          initialLoad={false}
          // pageStart={0}
          hasMore={feedbackDataState.hasMore}
          loadMore={loadFeedback}
          useWindow={false}
          loader={<Skeleton key={`00loaderFeedback${type}`} height="200px" />}
        >
          {feedbackDataState.data.map(
            (data, index) => (
              <div>
                <Feedback key={`appreciativeFeedback${index}`} data={data} isAppreciative={type === 'APPRECIATIVE'} />
                {index < feedbackDataState.data.length - 1 && <StyledDivider bottomGutter={1} />}
              </div>
            )
            // return <PostFeedItem feedId={data.id} employee={loggedInEmployee} />;
          )}
        </InfiniteScroll>
        {!loading ? (
          feedbackDataState.data.length === 0 ? (
            <NoFeedbacksMessage
              isCompetencySidePanel={isCompetencySidePanel}
              accessible={accessible}
              employeeId={employeeId}
              feedbackType={type}
              competencyId={competencyId}
              hideShareFeedbackButtons={hideShareFeedbackButtons}
              type={type}
            />
          ) : null
        ) : null}
      </FeedbackContainer>
    </Box>
  );
}

const FeedbackContainer = styled(Paper)`
  overflow: auto;
  ${HideScrollbarCSS};
  padding: ${(props) => props.theme.spacing(2)}rem;
  padding-top: ${(props) => props.theme.spacing(0.5)}rem;
  ${(props) =>
    props.$noHorizontalPadding &&
    css`
      padding-left: 0;
      padding-right: 0;
    `}

  box-shadow: none;
`;
